/**
 * Product Page V2 — Interactive Behavior
 * Vanilla ES6+ IIFE — no global scope pollution
 *
 * Modules:
 *   1. ImageSlider — auto-advance, dot nav, pause on hover
 *   2. ColorSelector — sync desktop/mobile, update images
 *   3. SizeSelector — highlight, price update, enable actions
 *   4. ActionButtons — add to cart, buy now
 *   5. Accordion — toggle open/close, one-at-a-time
 *   6. MobileFAB & Popup — open/close, body lock
 *   7. SizeGuide — modal open/close, focus trap
 *   8. Wishlist — toggle, localStorage persist
 */
(function () {
  'use strict';

  // ─── 3.1: Product Data Initialization ───────────────────────────────────────

  const dataEl = document.getElementById('ppv2-product-data');
  let productData;

  try {
    productData = JSON.parse(dataEl ? dataEl.textContent : '');
  } catch (e) {
    console.warn('PPV2: Product data not found');
    return;
  }

  if (!productData || !productData.variants || !productData.variants.length) {
    console.warn('PPV2: Product data not found');
    return;
  }

  // UI State
  const state = {
    selectedColor: null,
    selectedSize: null,
    selectedVariant: null,
    sliderIndex: 0,
    isPopupOpen: false,
    isSizeGuideOpen: false,
    isWishlisted: false,
    cartCount: 0,
    pendingAction: null // 'add' or 'buy' — stored when popup opens from FAB
  };

  // Dynamic option positions from Liquid
  const colorOptionPos = productData.color_option_position; // e.g., 1, 2, or null
  const sizeOptionPos = productData.size_option_position;   // e.g., 1, 2, or null
  const colorOptionKey = colorOptionPos ? 'option' + colorOptionPos : null;
  const sizeOptionKey = sizeOptionPos ? 'option' + sizeOptionPos : null;

  // Set initial color from selected variant
  if (colorOptionKey) {
    const selectedVariant = productData.variants.find(v => v.id === productData.selected_variant_id);
    state.selectedColor = selectedVariant ? selectedVariant[colorOptionKey] : productData.variants[0][colorOptionKey];
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const formatPrice = (price) => {
    // Use the store's money_format from Shopify (e.g., "₹ {{amount}}" or "Rs. {{amount_with_comma_separator}}")
    const moneyFormat = productData.money_format || '₹ {{amount}}';
    const cents = price;
    const dollars = (cents / 100).toFixed(2);
    const dollarsNoDecimals = Math.floor(cents / 100);
    const dollarsWithCommas = dollars.replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
    const dollarsWithCommasNoDecimals = dollarsNoDecimals.toString().replace(/(\d)(?=(\d{3})+$)/g, '$1,');

    // Indian comma format: 1,23,456.00
    const indianFormat = (cents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const indianFormatNoDecimals = Math.floor(cents / 100).toLocaleString('en-IN');

    return moneyFormat
      .replace('{{amount_with_comma_separator}}', dollarsWithCommas)
      .replace('{{amount_no_decimals_with_comma_separator}}', dollarsWithCommasNoDecimals)
      .replace('{{amount_no_decimals}}', dollarsNoDecimals)
      .replace('{{amount}}', dollars);
  };

  const getVariantsForColor = (color) => {
    if (!colorOptionKey || !color) return productData.variants;
    return productData.variants.filter((v) => v[colorOptionKey] === color);
  };

  const getImagesForColor = (color) => {
    const colorVariantIds = getVariantsForColor(color).map((v) => v.id);
    const filtered = productData.images.filter((img) =>
      img.variant_ids.some((vid) => colorVariantIds.includes(vid))
    );
    // Fallback: if no images matched, return all images
    return filtered.length > 0 ? filtered : productData.images;
  };

  const findVariant = (color, size) => {
    return productData.variants.find((v) => {
      const colorMatch = !colorOptionKey || !color || v[colorOptionKey] === color;
      const sizeMatch = !sizeOptionKey || !size || v[sizeOptionKey] === size;
      return colorMatch && sizeMatch;
    });
  };


  // ─── 3.2: ImageSlider Module ────────────────────────────────────────────────

  const ImageSlider = (() => {
    const slider = document.getElementById('ppv2-slider');
    const dotsContainer = document.getElementById('ppv2-slider-dots');
    const sliderContainer = slider ? slider.closest('.ppv2__slider-container') : null;

    if (!slider || !dotsContainer) return { updateImages: () => {} };

    let images = Array.from(slider.querySelectorAll('img'));
    let dots = Array.from(dotsContainer.querySelectorAll('.ppv2__slider-dot'));
    let intervalId = null;
    let isPaused = false;

    const goTo = (index) => {
      const total = images.length;
      if (total === 0) return;
      state.sliderIndex = ((index % total) + total) % total;
      slider.style.transform = `translateX(-${state.sliderIndex * 100}%)`;
      dots.forEach((dot, i) => {
        dot.classList.toggle('ppv2__slider-dot--active', i === state.sliderIndex);
      });
    };

    const advance = () => {
      goTo(state.sliderIndex + 1);
    };

    const startAutoAdvance = () => {
      stopAutoAdvance();
      intervalId = setInterval(() => {
        if (!isPaused) advance();
      }, 3000);
    };

    const stopAutoAdvance = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Pause on hover
    if (sliderContainer) {
      sliderContainer.addEventListener('mouseenter', () => {
        isPaused = true;
      });
      sliderContainer.addEventListener('mouseleave', () => {
        isPaused = false;
      });
    }

    // Dot click navigation
    dotsContainer.addEventListener('click', (e) => {
      const dot = e.target.closest('.ppv2__slider-dot');
      if (!dot) return;
      const index = parseInt(dot.dataset.index, 10);
      if (!isNaN(index)) goTo(index);
    });

    // Update images when color changes
    const updateImages = (imageUrls) => {
      // Rebuild slider images
      slider.innerHTML = '';
      imageUrls.forEach((img, i) => {
        const el = document.createElement('img');
        el.src = img.src;
        el.alt = img.alt || productData.title;
        if (i === 0) {
          el.setAttribute('fetchpriority', 'high');
        } else {
          el.loading = 'lazy';
        }
        slider.appendChild(el);
      });
      images = Array.from(slider.querySelectorAll('img'));

      // Rebuild dots
      dotsContainer.innerHTML = '';
      imageUrls.forEach((_, i) => {
        const dot = document.createElement('button');
        dot.className = 'ppv2__slider-dot' + (i === 0 ? ' ppv2__slider-dot--active' : '');
        dot.setAttribute('aria-label', `Go to image ${i + 1}`);
        dot.dataset.index = i;
        dotsContainer.appendChild(dot);
      });
      dots = Array.from(dotsContainer.querySelectorAll('.ppv2__slider-dot'));

      // Reset to first slide
      state.sliderIndex = 0;
      slider.style.transform = 'translateX(0%)';
    };

    startAutoAdvance();

    return { updateImages, goTo };
  })();


  // ─── Price Update Helper ────────────────────────────────────────────────────

  const updateAllPrices = (price, compareAtPrice) => {
    const priceEls = [
      document.getElementById('ppv2-desktop-price'),
      document.getElementById('ppv2-mobile-price'),
      document.getElementById('ppv2-popup-price')
    ];
    const compareEls = [
      document.getElementById('ppv2-desktop-compare'),
      document.getElementById('ppv2-mobile-compare'),
      document.getElementById('ppv2-popup-compare')
    ];

    const formattedPrice = formatPrice(price);
    priceEls.forEach((el) => {
      if (el) el.textContent = formattedPrice;
    });

    const hasCompare = compareAtPrice && compareAtPrice > price;
    const formattedCompare = hasCompare ? formatPrice(compareAtPrice) : '';
    compareEls.forEach((el) => {
      if (el) {
        el.textContent = formattedCompare;
        el.style.display = hasCompare ? '' : 'none';
      }
    });
  };

  // ─── 3.4: SizeSelector Module ──────────────────────────────────────────────

  const SizeSelector = (() => {
    const allSizeBtns = document.querySelectorAll('.ppv2__size-btn');
    const desktopAdd = document.getElementById('ppv2-desktop-add');
    const desktopBuy = document.getElementById('ppv2-desktop-buy');
    const popupAdd = document.getElementById('ppv2-popup-add');
    const popupBuy = document.getElementById('ppv2-popup-buy');
    const mobileAdd = document.getElementById('ppv2-mobile-add');
    const mobileBuy = document.getElementById('ppv2-mobile-buy');

    const actionBtns = [desktopAdd, desktopBuy, popupAdd, popupBuy, mobileAdd, mobileBuy].filter(Boolean);

    const enableActions = () => {
      actionBtns.forEach((btn) => {
        btn.classList.remove('ppv2__action-btn--disabled');
        btn.removeAttribute('disabled');
      });
    };

    const disableActions = () => {
      actionBtns.forEach((btn) => {
        btn.classList.add('ppv2__action-btn--disabled');
        btn.setAttribute('disabled', '');
      });
    };

    const selectSize = (size) => {
      state.selectedSize = size;

      // Highlight selected size across all grids
      allSizeBtns.forEach((btn) => {
        const isMatch = btn.dataset.size === size;
        btn.classList.toggle('ppv2__size-btn--selected', isMatch);
      });

      // Find matching variant
      const variant = findVariant(state.selectedColor, size);
      if (variant) {
        state.selectedVariant = variant;
        updateAllPrices(variant.price, variant.compare_at_price);
        enableActions();
      }
    };

    const reset = () => {
      state.selectedSize = null;
      state.selectedVariant = null;
      allSizeBtns.forEach((btn) => {
        btn.classList.remove('ppv2__size-btn--selected');
      });
      disableActions();
    };

    const updateAvailability = (color) => {
      const colorVariants = getVariantsForColor(color);
      allSizeBtns.forEach((btn) => {
        const size = btn.dataset.size;
        const variant = colorVariants.find((v) => sizeOptionKey ? v[sizeOptionKey] === size : false);
        const isAvailable = variant && variant.available;

        if (isAvailable) {
          btn.classList.remove('ppv2__size-btn--disabled');
          btn.removeAttribute('disabled');
          btn.removeAttribute('aria-disabled');
        } else {
          btn.classList.add('ppv2__size-btn--disabled');
          btn.setAttribute('disabled', '');
          btn.setAttribute('aria-disabled', 'true');
        }
      });
    };

    // Click handler
    allSizeBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('ppv2__size-btn--disabled')) return;
        const size = btn.dataset.size;
        if (size) selectSize(size);
      });
    });

    return { reset, updateAvailability, selectSize };
  })();

  // ─── 3.3: ColorSelector Module ──────────────────────────────────────────────

  const ColorSelector = (() => {
    const allColorBtns = document.querySelectorAll('.ppv2__color-btn');

    const selectColor = (color) => {
      state.selectedColor = color;

      // 1. Update all color buttons across desktop, mobile, and popup
      allColorBtns.forEach((btn) => {
        const isMatch = btn.dataset.color === color;
        btn.classList.toggle('ppv2__color-btn--selected', isMatch);
        const parent = btn.closest('.ppv2__color-item');
        if (parent) {
          parent.classList.toggle('ppv2__color-item--selected', isMatch);
        }
      });

      // 2. Update desktop slider images
      const colorImages = getImagesForColor(color);
      ImageSlider.updateImages(colorImages);

      // 3. Update mobile hero image
      const heroContainer = document.querySelector('.ppv2__hero');
      if (heroContainer && colorImages.length > 0) {
        const heroImg = heroContainer.querySelector('img');
        if (heroImg) {
          heroImg.src = colorImages[0].src;
          heroImg.alt = colorImages[0].alt || productData.title;
        }
      }

      // 4. Update mobile stacked images
      const stackedContainer = document.getElementById('ppv2-stacked-images');
      if (stackedContainer) {
        stackedContainer.innerHTML = '';
        colorImages.slice(1).forEach((img) => {
          const el = document.createElement('img');
          el.src = img.src;
          el.alt = img.alt || productData.title;
          el.loading = 'lazy';
          stackedContainer.appendChild(el);
        });
      }

      // 5. Reset size selection
      SizeSelector.reset();

      // 6. Update size availability for new color
      SizeSelector.updateAvailability(color);

      // 7. Update price to first available variant of selected color
      const colorVariants = getVariantsForColor(color);
      const firstAvailable = colorVariants.find((v) => v.available) || colorVariants[0];
      if (firstAvailable) {
        updateAllPrices(firstAvailable.price, firstAvailable.compare_at_price);
      }
    };

    // Event delegation — listen on all color buttons
    allColorBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const color = btn.dataset.color;
        if (color && color !== state.selectedColor) {
          selectColor(color);
        }
      });
    });

    return { selectColor };
  })();


  // ─── 3.6: Accordion Module ──────────────────────────────────────────────────

  const Accordion = (() => {
    const headers = document.querySelectorAll('.ppv2__accordion-header');

    headers.forEach((header) => {
      header.addEventListener('click', () => {
        const item = header.closest('.ppv2__accordion-item');
        const container = header.closest('.ppv2__accordions');
        if (!item || !container) return;

        const isOpen = item.classList.contains('ppv2__accordion-item--open');

        // Close all other open items in the same container
        container.querySelectorAll('.ppv2__accordion-item--open').forEach((openItem) => {
          if (openItem !== item) {
            openItem.classList.remove('ppv2__accordion-item--open');
            const openHeader = openItem.querySelector('.ppv2__accordion-header');
            if (openHeader) openHeader.setAttribute('aria-expanded', 'false');
          }
        });

        // Toggle current item
        item.classList.toggle('ppv2__accordion-item--open', !isOpen);
        header.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  })();


  // ─── 3.7: MobileFAB and Popup Module ────────────────────────────────────────

  const MobilePopup = (() => {
    const overlay = document.getElementById('ppv2-popup-overlay');
    const popup = document.getElementById('ppv2-popup');
    const closeBtn = document.getElementById('ppv2-popup-close');

    const open = () => {
      if (!overlay || !popup) return;
      state.isPopupOpen = true;
      overlay.classList.add('ppv2__popup-overlay--visible');
      popup.classList.add('ppv2__popup--visible');
      document.body.classList.add('ppv2-body-locked');
    };

    const close = () => {
      if (!overlay || !popup) return;
      state.isPopupOpen = false;
      overlay.classList.remove('ppv2__popup-overlay--visible');
      popup.classList.remove('ppv2__popup--visible');
      document.body.classList.remove('ppv2-body-locked');
      state.pendingAction = null;
    };

    // Close triggers
    if (overlay) overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);

    return { open, close };
  })();


  // ─── 3.5: ActionButtons Module (Add to Cart + Buy Now) ──────────────────────

  const ActionButtons = (() => {
    // Desktop and mobile each have their own error container; write to both so
    // the message shows in whichever layout is visible.
    const errorEls = [
      document.getElementById('ppv2-error'),
      document.getElementById('ppv2-error-mobile')
    ].filter(Boolean);

    const showError = (msg) => {
      errorEls.forEach((el) => {
        el.textContent = msg;
        el.style.display = 'block';
      });
      setTimeout(() => {
        errorEls.forEach((el) => {
          el.textContent = '';
          el.style.display = 'none';
        });
      }, 5000);
    };

    const clearError = () => {
      errorEls.forEach((el) => {
        el.textContent = '';
        el.style.display = 'none';
      });
    };

    const animateCartBadge = () => {
      const badge = document.getElementById('header-cart-count');
      if (!badge) return;
      const currentCount = parseInt(badge.textContent || '0', 10);
      const newCount = currentCount + 1;
      badge.textContent = newCount;
      state.cartCount = newCount;
      badge.style.display = 'flex';
      // Brief scale-up animation
      badge.style.transition = 'transform 0.2s ease';
      badge.style.transform = 'scale(1.4)';
      setTimeout(() => {
        badge.style.transform = 'scale(1)';
      }, 200);
    };

    // The recipient form is rendered twice (desktop + mobile layouts); only one
    // is visible at a time. Return whichever is currently shown.
    const getVisibleRecipientForm = () => {
      const forms = document.querySelectorAll('.recipient-form');
      for (const f of forms) {
        if (f.offsetParent !== null) return f; // visible (not display:none)
      }
      return forms[0] || null;
    };

    // Collect gift card recipient line-item properties (only when fields are
    // enabled — the web component disables them when "My email" is selected).
    const getGiftCardProperties = () => {
      const formEl = getVisibleRecipientForm();
      if (!formEl) return null;
      const properties = {};
      const inputs = formEl.querySelectorAll('[name^="properties["]:not([disabled])');
      inputs.forEach((input) => {
        const match = input.name.match(/^properties\[(.+)\]$/);
        if (!match) return;
        const key = match[1];
        const value = (input.value || '').trim();
        // Always include the control flag; skip other empty fields.
        if (value !== '' || key === '__shopify_send_gift_card_to_recipient') {
          properties[key] = value || 'on';
        }
      });
      return Object.keys(properties).length > 0 ? properties : null;
    };

    const buildLineItem = () => {
      const item = { id: state.selectedVariant.id, quantity: 1 };
      const properties = getGiftCardProperties();
      if (properties) item.properties = properties;
      return item;
    };

    // Returns true if the gift card recipient form is valid (or not applicable).
    const validateGiftCard = () => {
      const formEl = getVisibleRecipientForm();
      if (!formEl) return true;
      const emailInput = formEl.querySelector('input[name="properties[Recipient email]"]:not([disabled])');
      if (!emailInput) return true; // "My email" mode — nothing to validate
      if (!emailInput.checkValidity() || emailInput.value.trim() === '') {
        showError('Please enter a valid recipient email.');
        emailInput.focus();
        return false;
      }
      return true;
    };

    const addToCart = () => {
      if (!state.selectedVariant) return;
      clearError();
      if (!validateGiftCard()) return;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [buildLineItem()]
        })
      })
        .then((res) => {
          if (!res.ok) throw new Error('Cart error');
          return res.json();
        })
        .then(() => {
          animateCartBadge();
          // Dispatch custom event
          document.dispatchEvent(
            new CustomEvent('ppv2:cart-updated', {
              detail: { itemCount: state.cartCount }
            })
          );
        })
        .catch(() => {
          showError('Could not add to cart. Please try again.');
        });
    };

    const buyNow = () => {
      if (!state.selectedVariant) return;
      if (!validateGiftCard()) return;

      // Gift cards can carry recipient properties which the cart permalink
      // cannot represent — add to cart first, then go straight to checkout.
      const properties = getGiftCardProperties();
      if (properties) {
        clearError();
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [buildLineItem()] })
        })
          .then((res) => {
            if (!res.ok) throw new Error('Cart error');
            return res.json();
          })
          .then(() => {
            window.location.href = '/checkout';
          })
          .catch(() => {
            showError('Could not proceed to checkout. Please try again.');
          });
        return;
      }

      window.location.href = '/cart/' + state.selectedVariant.id + ':1';
    };

    // Desktop action buttons
    const desktopAdd = document.getElementById('ppv2-desktop-add');
    const desktopBuy = document.getElementById('ppv2-desktop-buy');
    if (desktopAdd) desktopAdd.addEventListener('click', addToCart);
    if (desktopBuy) desktopBuy.addEventListener('click', buyNow);

    // Mobile inline action buttons (gift cards use these instead of the FAB)
    const mobileAdd = document.getElementById('ppv2-mobile-add');
    const mobileBuy = document.getElementById('ppv2-mobile-buy');
    if (mobileAdd) mobileAdd.addEventListener('click', addToCart);
    if (mobileBuy) mobileBuy.addEventListener('click', buyNow);

    // Popup action buttons
    const popupAdd = document.getElementById('ppv2-popup-add');
    const popupBuy = document.getElementById('ppv2-popup-buy');
    if (popupAdd) {
      popupAdd.addEventListener('click', () => {
        addToCart();
        MobilePopup.close();
      });
    }
    if (popupBuy) {
      popupBuy.addEventListener('click', () => {
        buyNow();
        MobilePopup.close();
      });
    }

    // FAB buttons
    const fabAdd = document.getElementById('ppv2-fab-add');
    const fabBuy = document.getElementById('ppv2-fab-buy');
    if (fabAdd) {
      fabAdd.addEventListener('click', () => {
        if (!state.selectedSize) {
          state.pendingAction = 'add';
          MobilePopup.open();
        } else {
          addToCart();
        }
      });
    }
    if (fabBuy) {
      fabBuy.addEventListener('click', () => {
        if (!state.selectedSize) {
          state.pendingAction = 'buy';
          MobilePopup.open();
        } else {
          buyNow();
        }
      });
    }

    // Public API for the GoKwik buy-now buttons.
    // The native #ppv2-*-buy buttons are replaced by GoKwik's own button, so the
    // size-validation logic above never runs for them. Exposing this lets the
    // GoKwik integration honour the same rules: never start checkout without a
    // size, and open the size popup on mobile instead of bypassing it.
    window.ppv2 = window.ppv2 || {};
    window.ppv2.hasSize = () => !!state.selectedSize;
    window.ppv2.requiresSize = () => document.querySelectorAll('.ppv2__size-btn').length > 0;
    window.ppv2.openSizePopup = (action) => {
      state.pendingAction = action || 'buy';
      MobilePopup.open();
    };

    return { addToCart, buyNow };
  })();


  // ─── 3.8: SizeGuide Modal Module ────────────────────────────────────────────

  const SizeGuide = (() => {
    const modal = document.getElementById('ppv2-size-guide');
    const closeBtn = document.getElementById('ppv2-size-guide-close');
    const openBtns = document.querySelectorAll('.ppv2__size-guide-trigger');
    const body = modal ? modal.querySelector('.ppv2__size-guide-body') : null;
    const img = modal ? modal.querySelector('.ppv2__size-guide-img') : null;
    const zoomInBtn = document.getElementById('ppv2-size-guide-zoom-in');
    const zoomOutBtn = document.getElementById('ppv2-size-guide-zoom-out');

    if (!modal) return;

    let previousFocus = null;

    // ── Zoom state ──
    const ZOOM_MIN = 1;
    const ZOOM_MAX = 3;
    const ZOOM_STEP = 0.1;
    let zoom = 1;
    let baseWidth = 0; // fitted display width at zoom = 1 (measured while constrained)

    // Keep the view centered on the same point after a zoom change
    const centerScroll = () => {
      if (!body) return;
      body.scrollLeft = Math.max(0, (body.scrollWidth - body.clientWidth) / 2);
      body.scrollTop = Math.max(0, (body.scrollHeight - body.clientHeight) / 2);
    };

    const applyZoom = () => {
      if (!img || !body) return;
      // Round to avoid floating-point drift (e.g. 1.0000000002)
      zoom = Math.round(zoom * 10) / 10;

      if (zoom <= 1) {
        zoom = 1;
        img.style.width = '';
        img.style.height = '';
        body.classList.remove('is-zoomed');
      } else {
        // Measure the fitted width BEFORE removing the size constraints,
        // otherwise the base is already inflated and each step over-zooms.
        if (!baseWidth) {
          baseWidth = img.getBoundingClientRect().width || img.clientWidth || 320;
        }
        body.classList.add('is-zoomed');
        // Width only; height stays auto so aspect ratio is preserved and the
        // scale matches the percentage exactly.
        img.style.width = Math.round(baseWidth * zoom) + 'px';
        img.style.height = 'auto';
      }

      if (zoomInBtn) zoomInBtn.disabled = zoom >= ZOOM_MAX;
      if (zoomOutBtn) zoomOutBtn.disabled = zoom <= ZOOM_MIN;
      requestAnimationFrame(centerScroll);
    };

    const setZoom = (next) => {
      zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
      applyZoom();
    };

    const resetZoom = () => {
      zoom = 1;
      baseWidth = 0; // recompute on next zoom-in (image size can differ per product)
      applyZoom();
      if (body) { body.scrollTop = 0; body.scrollLeft = 0; }
    };

    const open = () => {
      state.isSizeGuideOpen = true;
      previousFocus = document.activeElement;
      modal.classList.add('ppv2__size-guide--visible');
      document.body.classList.add('ppv2-body-locked');
      resetZoom();
      // Focus the close button
      if (closeBtn) closeBtn.focus();
    };

    const close = () => {
      state.isSizeGuideOpen = false;
      modal.classList.remove('ppv2__size-guide--visible');
      document.body.classList.remove('ppv2-body-locked');
      resetZoom();
      // Restore focus
      if (previousFocus) previousFocus.focus();
    };

    // Zoom control triggers
    if (zoomInBtn) zoomInBtn.addEventListener('click', () => setZoom(zoom + ZOOM_STEP));
    if (zoomOutBtn) zoomOutBtn.addEventListener('click', () => setZoom(zoom - ZOOM_STEP));
    // Double-click / double-tap the image to toggle between 100% and 150%
    if (img) img.addEventListener('dblclick', () => setZoom(zoom > 1 ? 1 : 1.5));

    // Open triggers
    openBtns.forEach((btn) => btn.addEventListener('click', open));

    // Close triggers
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Close on modal background click (not on content)
    modal.addEventListener('click', (e) => {
      if (e.target === modal) close();
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && state.isSizeGuideOpen) {
        close();
      }
    });

    // Focus trap
    modal.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab' || !state.isSizeGuideOpen) return;

      const focusable = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  })();


  // ─── 3.9: Wishlist Module ───────────────────────────────────────────────────

  const Wishlist = (() => {
    const btn = document.getElementById('ppv2-wishlist-btn');
    if (!btn) return;

    const storageKey = `ppv2-wishlist-${productData.id}`;

    const setActive = (active) => {
      state.isWishlisted = active;
      btn.classList.toggle('ppv2__wishlist--active', active);
    };

    // Restore from localStorage
    const stored = localStorage.getItem(storageKey);
    if (stored === 'true') {
      setActive(true);
    }

    // Toggle on click
    btn.addEventListener('click', () => {
      const newState = !state.isWishlisted;
      setActive(newState);
      localStorage.setItem(storageKey, String(newState));
    });
  })();

  // ─── Escape key handler for popup ──────────────────────────────────────────

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && state.isPopupOpen) {
      MobilePopup.close();
    }
  });

  // ─── 3.10: Color Swatches Scroll Arrows ────────────────────────────────────
  // Show/hide-on-scroll behaviour now lives in the shared assets/scroll-arrows.js
  // (loaded alongside this file), so both the swatches here and the
  // subcategory header sub-collection row use the same logic.

})();
